import { useEffect, useMemo, useState } from "react";
import { Switch } from "@/components/ui/switch";
import { Spinner } from "@/components/ui/spinner";
import { PermissionScopeSection } from "./PermissionScopeSection";
import { PermissionPanelShell } from "./PermissionPanelShell";
import { PublishPermissionConfirmDialog } from "./PublishPermissionConfirmDialog";
import { usePermissionMatrix } from "@/features/permissions/hooks/usePermissionsQueries";
import { useUpdateRolePermissionTemplates } from "@/features/permissions/hooks/usePermissionsMutations";
import { requiresConfirmOnEnable } from "@/features/permissions/constants/permission-confirmations";
import { getRolePermissionsForPanel } from "@/features/permissions/utils/role-permissions";
import { getRoleLabel } from "@/features/roles/constants/role-labels";
import { useRoleLabelMap } from "@/features/roles/hooks/useRolesQueries";
import { useUserPermissions } from "@/hooks/useUserPermissions";
import { useDepartments } from "@/features/departments/hooks/useDepartmentsQueries";

export function RoleTemplatePanel({ editableRole }: { editableRole: string }) {
  const { data: matrix, isLoading } = usePermissionMatrix();
  const { data: departmentsData } = useDepartments({ limit: 200, isActive: true });
  const updateMutation = useUpdateRolePermissionTemplates();
  const roleLabelMap = useRoleLabelMap();

  const { canManagePermissions } = useUserPermissions();
  // A Sub-Admin defines content and document access only (FRD §5.3); the rest is Super Admin's.
  const rolePermissions = useMemo(
    () =>
      getRolePermissionsForPanel(editableRole).filter(
        (permission) =>
          canManagePermissions ||
          permission.startsWith("content:") ||
          permission.startsWith("documents:"),
      ),
    [editableRole, canManagePermissions],
  );

  const approvalDepartments = useMemo(
    () =>
      (departmentsData?.items ?? []).filter(
        (department) => department.requiresApproval,
      ),
    [departmentsData?.items],
  );

  const [localState, setLocalState] = useState<Record<string, boolean>>({});
  const [pendingPublishToggle, setPendingPublishToggle] = useState<
    (() => void) | null
  >(null);

  useEffect(() => {
    setLocalState({});
    setPendingPublishToggle(null);
  }, [editableRole]);

  const roleEffective = useMemo(
    () => new Set(matrix?.roleEffective?.[editableRole] ?? []),
    [matrix?.roleEffective, editableRole],
  );

  const isGranted = (perm: string) => {
    const local = localState[perm];
    if (local !== undefined) return local;
    return roleEffective.has(perm);
  };

  const applyToggle = (perm: string, checked: boolean) => {
    setLocalState((prev) => ({ ...prev, [perm]: checked }));
  };

  const handleToggle = (perm: string, checked: boolean) => {
    if (requiresConfirmOnEnable(perm, checked, isGranted(perm))) {
      setPendingPublishToggle(() => () => applyToggle(perm, checked));
      return;
    }
    applyToggle(perm, checked);
  };

  const dirtyCount = useMemo(() => {
    let count = 0;
    for (const perm of rolePermissions) {
      const effective = roleEffective.has(perm);
      const local = localState[perm];
      if (local !== undefined && local !== effective) count += 1;
    }
    return count;
  }, [rolePermissions, localState, roleEffective]);

  const handleSave = async () => {
    const entries = rolePermissions.map((permission) => ({
      permission,
      granted: isGranted(permission),
    }));

    await updateMutation.mutateAsync({ role: editableRole, entries });
    setLocalState({});
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner className="size-6" />
      </div>
    );
  }

  return (
    <>
      <PermissionPanelShell
        dirtyCount={dirtyCount}
        isPending={updateMutation.isPending}
        onSave={handleSave}
        saveLabel={`حفظ قالب ${getRoleLabel(editableRole, roleLabelMap)}`}
      >
        {rolePermissions.length > 0 ? (
          <PermissionScopeSection
            title="صلاحيات الدور"
            description="تُطبَّق على جميع مستخدمي هذا الدور — المحتوى، المستندات، الدعم"
            permissions={rolePermissions}
            activeCount={rolePermissions.filter(isGranted).length}
            isActive={isGranted}
            renderControl={(perm) => (
              <Switch
                checked={isGranted(perm)}
                onCheckedChange={(checked) => handleToggle(perm, checked)}
              />
            )}
          />
        ) : (
          <p className="text-right text-sm text-muted-foreground">
            لا توجد صلاحيات قابلة للتعديل لهذا الدور.
          </p>
        )}
      </PermissionPanelShell>

      <PublishPermissionConfirmDialog
        open={pendingPublishToggle !== null}
        mode="role"
        targetLabel={getRoleLabel(editableRole, roleLabelMap)}
        approvalDepartments={approvalDepartments}
        onConfirm={() => {
          pendingPublishToggle?.();
          setPendingPublishToggle(null);
        }}
        onCancel={() => setPendingPublishToggle(null)}
      />
    </>
  );
}
